import React, { useState } from 'react';
import { 
  Select, 
  MenuItem, 
  OutlinedInput, 
  FormControl, 
  Typography, 
  FormHelperText 
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const CustomSelect = ({
  label,
  placeholder,
  required = false,
  error = false,
  helperText,
  fullWidth = false,
  width,
  maxWidth,
  children,
  sx = {},
  display,
  MenuProps = {},
  value,
  ...props
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  
  // Calculate dynamic width
  const dynamicWidth = fullWidth ? '100%' : (width || 220);
  const dynamicMaxWidth = fullWidth ? '100%' : (maxWidth || width || 220);
  
  // Merge MenuProps
  const mergedMenuProps = {
    ...MenuProps,
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    PaperProps: {
      sx: {
        minWidth: 0, // Allow menu to size naturally
        width: 'auto', // Auto width based on content
        maxWidth: '100%', // But not wider than container
        maxHeight: 170, // Set maximum height to trigger scrolling
        boxShadow: 'none',
        borderRadius: '0 0 8px 8px',
        overflow: 'hidden',
        // fontSize: '14px',
        border: '1px solid #8C8C8C',
        borderTop: 'none',
        '& .MuiMenu-list': {
          padding: 0,
          maxHeight: 170, // Set max height for the menu list
          overflowY: 'auto', // Enable vertical scrolling
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c1c1c1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#a8a8a8',
          },
        },
        '& .MuiMenuItem-root': {
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          fontSize: '13px',
          minHeight: 'auto',
          padding: '8px 12px',
        },
        '& .Mui-selected': {
          backgroundColor: '#F7F7F7 !important',
          color:'#666666',
          textTransform: 'capitalize',
          fontSize: '13px',
        },
        ...MenuProps.PaperProps?.sx
      },
      ...MenuProps.PaperProps
    }
  };

  const selectComponent = (
    <Select
      {...props}
      value={value}
      displayEmpty
      open={menuOpen}
      onOpen={() => setMenuOpen(true)}
      onClose={() => setMenuOpen(false)}
      IconComponent={KeyboardArrowDownIcon}
      renderValue={(selected) => {
        if (!selected || selected === '') {
          return (
            <span style={{ color: '#999', fontSize: '16px' }}>
              {placeholder || 'Select an option'}
            </span>
          );
        }
        // Find the display text for the selected value
        const selectedChild = React.Children.toArray(children).find(
          child => child.props.value === selected
        );
        return selectedChild ? selectedChild.props.children : selected;
      }}
      input={
        <OutlinedInput
          error={error}
          sx={{
            // '& .MuiOutlinedInput': {
            //   borderColor: error ? '#d32f2f' : '#666',
            //   borderWidth: '1px',
            // },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? '#d32f2f' : '#8C8C8C !important',
              borderWidth: '1px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: error ? '#d32f2f !important' : '#666 !important',
              borderWidth: '1px',
            },
          }}
        />
      }
      sx={{
        '&.MuiOutlinedInput-root': {
          borderRadius: menuOpen ? '8px 8px 0px 0px': '8px',
          boxShadow: 'none',
          minHeight: '40px', // Match TextField height
          height: '40px', // Fixed height to match TextField
        },
        '& .MuiMenuItem-root': {
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }, 
        '& .MuiSelect-select': {
            padding: '8px 12px',
            paddingRight: '32px', // Space for dropdown icon
            fontSize: '16px',
            fontFamily: 'Lato, sans-serif',
            color: '#666',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        '& .Mui-selected': {
          backgroundColor: '#F7F7F7',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: error ? '#d32f2f' : '#8C8C8C',
          borderWidth: '1px',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: error ? '#d32f2f !important' : '#666 !important',
          borderWidth: '1px',
        },
        width: dynamicWidth,
        maxWidth: dynamicMaxWidth,
        ...sx,
      }}
      MenuProps={mergedMenuProps}
    >
      {children}
    </Select>
  );

  if (label || helperText) {
    return (
      <FormControl 
        fullWidth={fullWidth} 
        error={error}
        sx={{ 
          width: fullWidth ? '100%' : dynamicWidth,
          maxWidth: fullWidth ? '100%' : dynamicMaxWidth,
          display: display,
          gap: '4px',
        }}
      >
        {label && (
          <Typography  sx={{ 
            color: '#666',
            fontWeight: 700,
            fontSize: '13px',
            lineHeight: '20px',

          }} component="legend" required={required}>
            {label}
          </Typography>
        )}
        {selectComponent}
        {helperText && (
          <FormHelperText>
            {helperText}
          </FormHelperText>
        )}
      </FormControl>
    );
  }

  return selectComponent;
};

export default CustomSelect;
